import { Button, Card, useToast } from "@helmr/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { newIdempotencyKey } from "../../api/client";
import { createStaff } from "../../api/staff";
import { NoAccess } from "../../app/NoAccess";
import { useServices } from "../../app/services";
import { Field, FormError, messageOf, styles as c, useScope } from "../classes/common";
import { useRoleNames } from "./roles";

export function StaffNew() {
  const { client } = useServices();
  const sc = useScope();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const roles = useRoleNames();
  const key = useRef(newIdempotencyKey());
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [roleId, setRoleId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const branches = sc.ctx.me?.branches ?? [];
  const create = useMutation({
    mutationFn: () => createStaff(client, { name: name.trim(), phone: phone.trim(), roleId, homeBranchId: branchId || branches[0]?.id || sc.ctx.me?.branch?.id || "" }, key.current),
    onSuccess: (r) => { key.current = newIdempotencyKey(); void qc.invalidateQueries({ queryKey: ["staff"] }); toast.show(r.created ? "Staff added" : "That person is already on your team"); nav(`/staff/${r.staff.id}`); },
    onError: (e) => setError(messageOf(e)),
  });
  if (sc.ctx.status !== "loading" && !sc.ctx.has("staff.add")) return <NoAccess />;
  const submit = () => {
    if (!name.trim()) return setError("Enter their name.");
    if (phone.replace(/\D/g, "").length < 7) return setError("Enter a valid mobile number.");
    if (!roleId) return setError("Choose a role.");
    setError(null);
    create.mutate();
  };
  return (
    <div className={c.page}>
      <div className={c.head}><div><h1>Add staff</h1><p>They sign in with this mobile number.</p></div><Button as={Link} to="/staff">Back to staff</Button></div>
      <Card aria-label="New staff member">
        <form className={`${c.pad} ${c.stack}`} onSubmit={(e) => { e.preventDefault(); submit(); }} noValidate>
          <Field label="Name" htmlFor="st-name"><input id="st-name" className={c.input} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Mobile number" htmlFor="st-phone"><input id="st-phone" className={c.input} inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} /></Field>
          <Field label="Role" htmlFor="st-role" hint={roles.canPick ? "You can give a role only if you hold everything it allows." : "Choosing a role needs access to Roles, which your role does not have."}>
            <select id="st-role" className={c.input} value={roleId} disabled={!roles.canPick} onChange={(e) => setRoleId(e.target.value)}>
              <option value="">Choose a role</option>
              {roles.roles.filter((r) => !r.isOwner).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </Field>
          <Field label="Home branch" htmlFor="st-branch">
            <select id="st-branch" className={c.input} value={branchId || branches[0]?.id || ""} onChange={(e) => setBranchId(e.target.value)}>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select>
          </Field>
          <FormError message={error} />
          <div className={c.row}><Button variant="primary" type="submit" busy={create.isPending} busyLabel="Adding…">Add staff</Button></div>
        </form>
      </Card>
    </div>
  );
}
